'use client'

import { useEffect, useState } from 'react'
import { ArrowRight, Info } from 'lucide-react'
import { motion } from 'motion/react'

import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@ui/web/components'
import { CHILD_SIGN_IN_GUIDE_URL, FULL_GUIDE_URL } from '@helper/utils/core'
import { buildUrl } from '@helper/utils/format'
import { useLocalStorage } from '@helper/web/hooks'

import { useAuthClient } from '../context'

export interface OnboardingDialogProps {
  type: 'school' | 'web'
}

function LinkButton({ href, text, target }: { href: string; text: string; target?: React.HTMLAttributeAnchorTarget }) {
  return (
    <a href={href} target={target}>
      <motion.button
        className='srd:flex srd:items-center srd:gap-x-2 srd:flex-nowrap srd:border srd:w-full srd:border-main-blue-100 srd:text-neutrals-black srd:text-base srd:rounded-sm srd:px-2 srd:py-1 srd:text-left'
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        tabIndex={-1}
      >
        <ArrowRight className='srd:text-main-blue-100 srd:w-8 srd:h-8' />
        {text}
      </motion.button>
    </a>
  )
}

export function OnboardingDialog({ type }: OnboardingDialogProps) {
  const [isOpen, setIsOpen] = useState(false)

  const authClient = useAuthClient()

  const { data: sessionData } = authClient.useSession()

  const [neverShowOnboardingDialog, setNeverShowOnboardingDialog] = useLocalStorage('neverShowOnboardingDialog', false)

  useEffect(() => {
    const shouldShowPopup = !neverShowOnboardingDialog

    if (shouldShowPopup && sessionData?.user.userType === 'main') {
      setIsOpen(true)
    }
  }, [sessionData?.user.userType, neverShowOnboardingDialog])

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className='srd:w-full srd:max-w-lg srd:max-h-[95dvh] srd:overflow-y-auto srd:flex srd:flex-col srd:gap-y-8 srd:text-neutrals-black'>
        <DialogHeader className='srd:flex srd:flex-col srd:gap-y-2 srd:items-center srd:justify-center srd:text-center'>
          <DialogTitle className='srd:font-semibold srd:text-lg'>Selamat Datang</DialogTitle>
          <DialogDescription className='srd:sr-only'>Selamat Datang</DialogDescription>
        </DialogHeader>

        <header className='srd:flex srd:flex-col srd:gap-y-2 srd:items-center srd:justify-center srd:text-center'>
          <h2 className='srd:text-2xl srd:font-black'>Hai kamu pembina belajar!</h2>

          <h6 className='srd:text-base'>Kami senang kamu bergabung. Mari bersiap untuk memulai sekarang</h6>
        </header>

        <section className='srd:flex srd:flex-col srd:gap-y-4'>
          <section className='srd:flex srd:flex-col srd:gap-y-3 srd:w-full'>
            <section className='srd:flex srd:flex-col srd:gap-y-2'>
              <h4 className='srd:font-semibold srd:text-lg'>1. Coba sebagai anak</h4>
              <p className='srd:text-base'>Rasakan PiBo melalui sudut pandang pengguna anak.</p>
            </section>

            <section className='srd:flex srd:flex-col srd:gap-y-2'>
              <LinkButton
                href={
                  type === 'school'
                    ? buildUrl(type, '/pengaturan/kelola-anggota/murid').href
                    : buildUrl(type, '/dasbor', { tab: 'siswa-saya' }).href
                }
                text={type === 'school' ? 'Buat akun murid' : 'Buat akun anak'}
              />

              <LinkButton
                href={CHILD_SIGN_IN_GUIDE_URL}
                text='Masuk sebagai pengguna anak dan mulai baca.'
                target='_blank'
              />
            </section>
          </section>

          <section className='srd:flex srd:flex-col srd:gap-y-3 srd:w-full'>
            <section className='srd:flex srd:flex-col srd:gap-y-2'>
              <h4 className='srd:font-semibold srd:text-lg'>2. Periksa kemajuan mereka</h4>
              <p className='srd:text-base'>
                Kembali ke akunmu dan lihat bagaimana PiBo melacak pembelajaran yang sesungguhnya.
              </p>
            </section>

            <section className='srd:flex srd:flex-col srd:gap-y-2'>
              <LinkButton
                href={
                  type === 'school'
                    ? buildUrl(type, '/laporan-siswa').href
                    : buildUrl(type, '/dasbor', { tab: 'siswa-saya' }).href
                }
                text='Cek rapor dan profil anak'
              />
            </section>
          </section>

          <section className='srd:flex srd:flex-col srd:gap-y-3 srd:w-full'>
            <section className='srd:flex srd:flex-col srd:gap-y-2'>
              <h4 className='srd:font-semibold srd:text-lg'>3. Siapkan akses ke lebih banyak anak</h4>
              <p className='srd:text-base'>
                Mari kita wujudkan untuk seluruh {type === 'school' ? 'kelas' : 'keluargamu'}.
              </p>
            </section>

            <section className='srd:flex srd:flex-col srd:gap-y-2'>
              <LinkButton href={buildUrl(type, '/checkout/new').href} text='Tingkatkan layanan kapan saja' />
            </section>
          </section>
        </section>

        <div className='srd:w-full srd:flex srd:items-center srd:justify-center srd:text-center'>
          <Button
            variant='link'
            className='srd:text-main-blue-100 srd:hover:text-main-blue-100 srd:font-medium srd:text-xs srd:border-none srd:p-0 srd:w-fit srd:h-fit'
            onClick={() => {
              setNeverShowOnboardingDialog(true)
              setIsOpen(false)
            }}
            type='button'
          >
            Jangan tampilkan ini lagi
          </Button>
        </div>

        <div className='srd:w-full srd:flex srd:items-center srd:justify-center srd:sm:flex-row srd:flex-col-reverse srd:gap-4'>
          <a href={FULL_GUIDE_URL} target='_blank' className='srd:w-full srd:flex-1 srd:text-nowrap'>
            <Button
              className='srd:w-full srd:text-main-blue-100 srd:font-medium srd:text-base srd:bg-none srd:bg-transparent srd:rounded-sm'
              tabIndex={-1}
              type='button'
            >
              <Info className='srd:w-4 srd:h-4 srd:mr-2' />
              Lihat panduan lengkap
            </Button>
          </a>

          <Button variant='outline' type='button' onClick={() => setIsOpen(false)}>
            Nanti saja
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
